import React, { useReducer } from 'react'
import * as R from 'ramda'
import styled, { createGlobalStyle } from 'styled-components'

import FilterGroupLayout from './layout'
import { FilterDropdown } from 'components/FilterDropdown'
import { FilterItem } from 'components/FilterItem'
import { LinkButton } from 'elements/LinkButton'
import { Margin } from 'elements/Margin'
import { FilterItemLayout } from 'components/FilterItem/layout'
import filterReducer from './reducer'
import Popup from 'shared/components/Popup'
import { COMPARISONS } from 'components/FilterValuesPopover'
import { getRuleDiscriminator } from 'libs/filter-rule-discriminator'
import './styles.css'

export default FilterGroup

/** @typedef {import('shared/components/FilterGroup/reducer').Filter} Filter */
/**
 * @template T
 * @typedef {import('shared/components/FilterGroup/reducer').InputProps<T>} InputProps
 */
/**
 * @template T
 * @typedef {import('./reducer').Reducer<T>} Reducer
 */
/**
 * @template T
 * @typedef {import('./reducer').Rule<T>[]} Rules
 */
/**
 * @template T
 * @typedef {import('./reducer').State<T>} State
 */

/**
 * @template T
 * @typedef Option
 * @property {string} field
 * @property {string} label
 * @property {import('shared/components/KebabMenu').IconValue | 'fa-user-ninja' | 'fa-user-tie'} icon
 * @property {string} comparison
 * @property {Filter[]} [filters]
 * @property {boolean} [openPopoverOnAdd]
 * @property {T} [extra] additional keys to pass to onChange value
 */

/**
 * @template T
 * @typedef OnChangeRule
 * @property {string} field
 * @property {string} label
 * @property {import('semantic-ui-react').SemanticICONS} icon
 * @property {string} comparison
 * @property {Filter[]} filters
 * @property {T} [extra] additional keys passed from options
 */

/**
 * @template T
 * @typedef OnChangeValue
 * @property {'and' | 'or'} condition
 * @property {OnChangeRule<T>[]} rules
 * @property {OnChangeRule<T>[]} globalOperator
 * @property {OnChangeRule<T>[]} groups
 */

/**
 * @template T
 * @param {object} props
 * @param {Option<T>[]} props.options
 * @param {(value: OnChangeValue<T>) => void} props.onChange
 * @param {'and' | 'or'} props.condition
 * @param {OnChangeRule<T>[]} [props.defaultValue]
 * @param {Reducer<T>} props._reducer dependency injection via default Props, because we can't explicitly set the generic type for the dependencies
 * @param {object=} props.style
 * @param {object[]=} props.comparisons
 * @param {boolean=} props.allowKeepingSelectedOption
 */
function FilterGroup({
  options,
  condition,
  onChange,
  defaultValue,
  _reducer,
  style,
  comparisons = COMPARISONS,
  allowKeepingSelectedOption,
}) {
  const initialState = getInitialState(defaultValue)
  const [state, dispatch] = useReducer(_reducer, initialState)
  const actions = filterReducer.createActions(dispatch)

  const { rules, isDropdownOpen } = state

  const usedFields = rules.map((rule) => rule.field + getRuleDiscriminator(rule))
  const optionsWithoutResource = R.reject(checkIsResource, options)
  const availableOptions = allowKeepingSelectedOption
    ? optionsWithoutResource
    : R.reject(fieldIn(usedFields), optionsWithoutResource)

  function handleAddFilter(rule) {
    actions.closeDropdown()
    actions.addRule(rule)
  }

  const atLeastOneRuleIsEmpty = Boolean(rules.find((rule) => R.isEmpty(rule.filters)))
  const canAddMoreFilter = !atLeastOneRuleIsEmpty

  const handleOnChange = (rulesArray) => {
    if (shouldCallOnChange(rulesArray)) {
      const onChangeValue = {
        condition,
        rules: rulesArray.map((rule) => {
          const { comparison, field, filters, icon, label, extra } = rule
          return {
            field,
            label,
            icon,
            comparison,
            filters,
            extra,
          }
        }),
      }
      onChange(onChangeValue)
    }
  }

  return (
    <span style={style}>
      <SmallerDropdown />
      <FilterItemLayout>
        {rules.map((rule, index) => {
          return (
            <FeedbackItems
              key={index}
              rule={rule}
              index={index}
              state={state}
              actions={actions}
              condition={condition}
              comparisons={comparisons}
              onClosePopover={handleOnChange}
            />
          )
        })}
      </FilterItemLayout>
      <InlineContainer data-html2canvas-ignore="true">
        <Popup
          basic
          tooltip="One of your filters is missing a value. Finish editing it before adding another."
          disabled={canAddMoreFilter}
        >
          <LinkButton
            onClick={(e) => {
              e.preventDefault()
              actions.clickAddRule()
            }}
            disabled={!canAddMoreFilter}
            className="ss-filter-group-add-button"
          >
            <i className="icon far fa-plus" />
            Add filter
          </LinkButton>
        </Popup>
        <FilterDropdown
          open={isDropdownOpen}
          options={availableOptions}
          onClose={actions.closeDropdown}
          onFilterAdd={handleAddFilter}
        />
      </InlineContainer>
    </span>
  )
}

FilterGroup.defaultProps = {
  _reducer: filterReducer.reducer,
}

/**
 * @template T
 * @param {OnChangeRule<T>[]=} defaultValue
 * @return {State<T>}
 */
function getInitialState(defaultValue) {
  if (!defaultValue) {
    return filterReducer.initialState
  }

  const rules = defaultValue
  const initialState = { ...filterReducer.initialState, rules }
  // @ts-ignore
  return initialState
}

/**
 * @template T
 * @param {Rules<T>} rules */
function shouldCallOnChange(rules) {
  return rules.every((rule) => {
    return rule.filters.length > 0
  })
}

function FeedbackItems({ rule, index, state, actions, condition, comparisons, onClosePopover }) {
  const open = index === state.activePopoverIndex
  const { rules = [] } = state
  function handleFilterChange(rule) {
    actions.setFilter(rule)
  }

  function handleComparisonChange(comparison) {
    actions.setComparison(comparison)
  }

  function handleExtraChange(data) {
    actions.setExtra(data)
  }

  function handleClick() {
    if (!open) {
      actions.openPopover(index)
    }
  }

  function handleRemove() {
    actions.removeRule(index)
    const newRules = R.remove(index, 1, [...rules])
    onClosePopover(newRules)
  }

  function handleClosePopover() {
    requestAnimationFrame(() => {
      actions.closePopover(index)
      onClosePopover(rules)
    })
  }

  return (
    <React.Fragment key={index}>
      {index !== 0 && (
        <Margin left={8} right={8}>
          <strong>{condition}</strong>
        </Margin>
      )}
      <FilterItem
        // @ts-ignore
        rule={rule}
        open={open}
        onClick={handleClick}
        onChangeValue={handleFilterChange}
        onRemove={handleRemove}
        onPopoverClose={handleClosePopover}
        onComparisonChange={handleComparisonChange}
        comparisons={comparisons}
        onExtraChange={handleExtraChange}
      />
    </React.Fragment>
  )
}

const InlineContainer = styled.div`
  display: inline-block;
`

const SmallerDropdown = createGlobalStyle`
  .ui.selection.dropdown {
    > .label, .menu > .item {
      font-size: 14px;
    }
  }
`

function fieldIn(rules) {
  return (rule) => R.find(R.equals(rule.field + getRuleDiscriminator(rule)), rules)
}

/**
 *
 * @param {object} option
 * @param {string} option.label
 */
function checkIsResource(option) {
  return R.includes(option.label, ['Resources', 'Resource'])
}

FilterGroup.Layout = FilterGroupLayout



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/shared/components/FilterGroup/index.js